import React, { useMemo } from "react";
import { Link } from "react-router-dom";
import {
  ArrowTopRightOnSquareIcon,
  ChevronRightIcon,
} from "@heroicons/react/24/outline";
import { Badge, Button, buttonVariants } from "../../components/ui";
import { cn } from "../../shared/lib/cn";
import type { CloseCalendarItem, CloseCalendarPeriod } from "./types";
import {
  computePipelineStages,
  formatStatus,
  getCloseCalendarCompletionSummary,
  getCloseCalendarStatusVariant,
  resolveActionLabel,
} from "./utils";

function PipelineNode({
  item,
  onApprove,
  isApproving,
}: {
  item: CloseCalendarItem;
  onApprove: (id: number) => void;
  isApproving: boolean;
}) {
  const isComplete = ["complete", "approved", "closed", "overridden"].includes(
    item.status,
  );
  const isBlocked = item.status === "blocked";
  const isReview = item.status === "needs_review";
  const isRunning = item.status === "running";
  const isWaiting =
    !isComplete &&
    !isBlocked &&
    !isReview &&
    !isRunning &&
    item.status !== "ready";
  const appletRoute = item.owning_applet_route;

  return (
    <div
      className={cn(
        "flex w-56 flex-col rounded-xl border p-3 transition-all",
        isComplete &&
          "border-emerald-200 bg-emerald-50/50 dark:border-emerald-900 dark:bg-emerald-950/20",
        isReview &&
          "border-amber-300 bg-amber-50/60 shadow-sm dark:border-amber-800 dark:bg-amber-950/20",
        isBlocked &&
          "border-rose-300 bg-rose-50/60 dark:border-rose-800 dark:bg-rose-950/20",
        isRunning &&
          "border-blue-300 bg-blue-50/40 dark:border-blue-800 dark:bg-blue-950/20",
        item.status === "ready" &&
          "border-gray-300 bg-white dark:border-gray-700 dark:bg-dark-surface",
        isWaiting &&
          "border-gray-200 bg-white opacity-50 dark:border-gray-800 dark:bg-dark-surface",
      )}
    >
      <div className="mb-2 flex items-start justify-between gap-1">
        <Badge
          variant={getCloseCalendarStatusVariant(item.status)}
          className="text-[10px] leading-none"
        >
          {formatStatus(item.status)}
        </Badge>
        {item.relative_day && (
          <span className="shrink-0 text-[10px] text-gray-400 dark:text-gray-500">
            {item.relative_day}
          </span>
        )}
      </div>

      <div className="flex-1 space-y-0.5">
        {appletRoute ? (
          <Link
            to={appletRoute}
            className="block text-sm font-semibold leading-tight text-charcoal hover:underline dark:text-gray-100"
          >
            {item.name}
          </Link>
        ) : (
          <p className="text-sm font-semibold leading-tight text-charcoal dark:text-gray-100">
            {item.name}
          </p>
        )}
        {item.owner_role && (
          <p className="text-xs text-gray-500 dark:text-gray-400">
            {item.owner_role}
          </p>
        )}
        {item.scheduled_complete_label && (
          <p className="text-[11px] text-gray-400 dark:text-gray-500">
            {item.scheduled_complete_label}
          </p>
        )}
        {item.blocker_reason && (
          <p className="mt-1.5 text-xs font-medium text-rose-600 dark:text-rose-400">
            {item.blocker_reason}
          </p>
        )}
      </div>

      {appletRoute && !isWaiting && (
        <div className="mt-3">
          {isReview ? (
            <Button
              size="sm"
              className="w-full min-w-0 justify-center overflow-hidden"
              onClick={() => onApprove(item.id)}
              disabled={isApproving}
            >
              <span className="min-w-0 truncate">
                {resolveActionLabel(item)}
              </span>
            </Button>
          ) : (
            <Link
              to={appletRoute}
              className={cn(
                buttonVariants({ variant: "secondary", size: "sm" }),
                "w-full min-w-0 justify-center overflow-hidden",
              )}
            >
              <ArrowTopRightOnSquareIcon className="mr-1.5 h-3.5 w-3.5 shrink-0" />
              <span className="min-w-0 truncate">
                {resolveActionLabel(item)}
              </span>
            </Link>
          )}
        </div>
      )}
    </div>
  );
}

function StageConnector({ nextStageSize }: { nextStageSize: number }) {
  return (
    <div
      className={cn(
        "flex shrink-0 items-center px-1.5",
        nextStageSize > 1 ? "self-start mt-[52px]" : "self-center",
      )}
    >
      <div className="h-px w-5 bg-gray-300 dark:bg-gray-700" />
      <ChevronRightIcon className="-ml-px h-3.5 w-3.5 text-gray-400 dark:text-gray-600" />
    </div>
  );
}

export function ClosePeriodStatus({ period }: { period: CloseCalendarPeriod }) {
  const summary = useMemo(
    () => getCloseCalendarCompletionSummary(period),
    [period],
  );
  const progressPct =
    summary.total > 0
      ? Math.round((summary.complete / summary.total) * 100)
      : 0;
  const onTrack = summary.blocked === 0 && summary.needsReview === 0;

  return (
    <div className="flex flex-wrap items-center justify-between gap-4 rounded-xl border border-gray-200 bg-white px-5 py-3.5 dark:border-gray-800 dark:bg-dark-surface">
      <div className="flex items-baseline gap-2.5">
        <span className="font-subtitle text-base text-charcoal dark:text-gray-100">
          {period.close_period} Close
        </span>
        <span className="text-sm text-gray-400 dark:text-gray-500">
          {period.template_name}
        </span>
      </div>
      <div className="flex items-center gap-3">
        <div className="flex items-center gap-2">
          <div className="h-1.5 w-24 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
            <div
              className="h-full rounded-full bg-emerald-500 transition-all duration-500"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <span className="text-xs tabular-nums text-gray-500 dark:text-gray-400">
            {summary.complete} / {summary.total}
          </span>
        </div>
        {summary.blocked > 0 && (
          <span className="rounded-full bg-rose-100 px-2.5 py-0.5 text-xs font-medium text-rose-700 dark:bg-rose-950/40 dark:text-rose-300">
            {summary.blocked} blocked
          </span>
        )}
        {summary.needsReview > 0 && (
          <span className="rounded-full bg-amber-100 px-2.5 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-950/40 dark:text-amber-300">
            {summary.needsReview} needs review
          </span>
        )}
        {onTrack && summary.complete > 0 && (
          <span className="rounded-full bg-emerald-100 px-2.5 py-0.5 text-xs font-medium text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300">
            On track
          </span>
        )}
      </div>
    </div>
  );
}

export function PipelineView({
  items,
  onApprove,
  isApproving,
}: {
  items: CloseCalendarItem[];
  onApprove: (id: number) => void;
  isApproving: boolean;
}) {
  const stages = useMemo(() => computePipelineStages(items), [items]);

  if (stages.length === 0) {
    return (
      <div className="flex min-h-[200px] items-center justify-center text-sm text-gray-400 dark:text-gray-600">
        No close items configured for this period.
      </div>
    );
  }

  return (
    <div
      data-testid="close-calendar-pipeline-scroll"
      className="min-h-[280px] overflow-x-auto overflow-y-visible overscroll-x-contain pb-4"
    >
      <div className="flex min-w-max items-start gap-0 py-1 pr-3">
        {stages.map((stage, stageIndex) => (
          <React.Fragment key={stageIndex}>
            <div className="flex flex-col gap-3">
              {stage.map((item) => (
                <PipelineNode
                  key={item.id}
                  item={item}
                  onApprove={onApprove}
                  isApproving={isApproving}
                />
              ))}
            </div>
            {stageIndex < stages.length - 1 && (
              <StageConnector nextStageSize={stages[stageIndex + 1].length} />
            )}
          </React.Fragment>
        ))}
      </div>
    </div>
  );
}
